/**
 * KAN-1003: публичная страница /prices (ЮKassa: "актуальные цены, что и
 * сколько платит клиент"). Цены и границы пополнения приходят из
 * GET /api/v1/public/prices/, постоянный текст лежит в src/content/legal/prices.ts.
 */
import { Loader2 } from 'lucide-react';
import { LegalPageShell } from '@/components/legal/LegalPageShell';
import { LegalParagraphText } from '@/components/legal/LegalText';
import { PRICES_HOW_IT_WORKS, PRICES_INTRO, PRICES_PAYMENT, PRICES_TITLE } from '@/content/legal/prices';
import { SUPPORT_EMAIL } from '@/content/legal/support';
import { usePublicPrices } from '@/hooks/usePublicPrices';
import { formatRub, formatWholeRub } from '@/lib/wallet';
import type { PublicPriceResource, PublicPricesTopup } from '@/types/publicPrices';

function PriceLine({ resource }: { resource: PublicPriceResource }) {
  return (
    <li
      data-testid={`price-line-${resource.code}`}
      className="flex flex-col gap-1 py-3 sm:flex-row sm:items-baseline sm:justify-between"
    >
      <span className="font-medium text-gray-900">{resource.title}</span>
      <span className="text-gray-700">
        <span className="font-semibold">{formatRub(resource.price_rub)}</span>{' '}
        <span className="text-gray-500">за {resource.unit}</span>
      </span>
    </li>
  );
}

function TopupLimits({ topup }: { topup: PublicPricesTopup }) {
  return (
    <p className="leading-relaxed text-gray-700" data-testid="prices-topup-limits">
      Сумма одного пополнения кошелька: от {formatWholeRub(topup.min_rub)} ₽ до{' '}
      {formatWholeRub(topup.max_rub)} ₽.
    </p>
  );
}

function PricesContent() {
  const { data, isLoading, isError } = usePublicPrices();

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 text-gray-500">
        <Loader2 className="h-4 w-4 animate-spin" />
        Загружаем цены...
      </div>
    );
  }
  if (isError || !data) {
    return (
      <p className="text-gray-700">
        Не удалось загрузить цены. Обновите страницу или напишите в поддержку: {SUPPORT_EMAIL}.
      </p>
    );
  }

  return (
    <div className="space-y-6">
      <ul className="divide-y divide-gray-200 border-y border-gray-200">
        {data.resources.map((resource) => (
          <PriceLine key={resource.code} resource={resource} />
        ))}
      </ul>
      <TopupLimits topup={data.topup} />
    </div>
  );
}

export function PricesPage() {
  return (
    <LegalPageShell documentTitle={PRICES_TITLE}>
      <article className="space-y-6">
        <header className="space-y-1">
          <h1 className="text-2xl font-bold text-gray-900 sm:text-3xl">{PRICES_TITLE}</h1>
          <p className="text-sm text-gray-500">{PRICES_INTRO}</p>
        </header>
        <PricesContent />
        <section className="space-y-3">
          <h2 className="text-lg font-semibold text-gray-900">Как списывается расход</h2>
          {PRICES_HOW_IT_WORKS.map((paragraph, i) => (
            <LegalParagraphText key={i} paragraph={paragraph} />
          ))}
        </section>
        <section className="space-y-3">
          <h2 className="text-lg font-semibold text-gray-900">Оплата и возврат</h2>
          {PRICES_PAYMENT.map((paragraph, i) => (
            <LegalParagraphText key={i} paragraph={paragraph} />
          ))}
        </section>
      </article>
    </LegalPageShell>
  );
}

export default PricesPage;
